import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { FreightTrackingsDetailValue } from "../models/invoice-preview/FreightTrackingsDetailValue";
import { PostFreightTrackingsModel } from "../models/invoice-preview/PostFreightTrackingsModel";
import { InvoiceEmail } from "../models/orders/invoicedEmail";
import { ProcessTrackingDetailsRequestDto } from "../models/orders/processTrackingDetailsRequestDto";
import { ProcessTrackingDetailsResponseDto } from "../models/orders/processTrackingDetailsResponseDto";
import { PrintPurchaseOrderDto } from "../models/orders/purchaseOrderLine";
import { SalesOrderNotes } from "../models/orders/salesOrderNotes";
import { OdataDmlResponse } from "../models/users/odataDmlResponse";
import { GetUserDto } from "../models/users/users/getUserDto";
import { InvoicePreviewLines } from "../models/invoice-preview/InvoicePreviewLines";
import { OrderNotes } from "../models/invoice-review/orderNotes";
import { InvoicePreviewDetailDto } from "../models/invoice-preview/InvoicePreviewDetailDto";
import { InvoiceProposalDto } from "../models/invoice-preview/invoiceProposalDto";
import { ProposalListRequestDto } from "../models/invoice-review/proposalListRequestDto";
import { UpdateProjectInvoicePreviewDispositionStatusResponseDto } from "../models/invoice-preview/updateProjectInvoicePreviewDispositionStatusResponseDto";
import { UpdateProjectInvoicePreviewStatusResponseDto } from "../models/invoice-preview/updateProjectInvoicePreviewStatusResponseDto";
import { FreightChargesInvoicePreviewDetailValue } from "../models/invoice-review/freightChargesInvoicePreviewDetailValue";
import { IpClientDepositDetailValue } from "../models/invoice-review/IpClientDepositDetailValue";
import { CreateSalesOrderEmailResponseDto } from "../models/invoice-review/createSalesOrderEmailResponseDto";
import { FreightBillingsDetailValue } from "../models/invoice-review/FreightBillingsDetailValue";
import { GetBillingDetailsForEmailsDto } from "../models/invoice-review/getBillingDetailsForEmailsDto";
import { ProjectInvoicePrintPreviewReportRequestDto } from "../models/invoice-preview/projectInvoicePrintPreviewReportRequestDto";
import { ProjectInvoicePrintPreviewReportResponseDto } from "../models/invoice-preview/projectInvoicePrintPreviewReportResponseDto";
import { UpdateInvoicePreviewStatusDto } from "../models/invoice-preview/updateInvoicePreviewStatusDto";
import { UpdateInvoicePreviewDispositionStatusDto } from "../models/invoice-preview/updateInvoicePreviewDispositionStatusDto";
import { TrackingFreightApiRequestDto } from "../models/invoice-preview/trackingFreightApiRequestDto";
import { TrackingFreightApiResponseDto } from "../models/invoice-preview/trackingFreightApiResponseDto";

@Injectable({
  providedIn: "root",
})
export class InvoicePreviewSummaryService {
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";

  private userManagementBaseURL = environment.baseUrl + "/User";

  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private invoicePreviewBaseURL = environment.baseUrl + "/InvoicePreview";
  constructor(private _http: HttpClient) {}

  getInvoicePreviewSummary(
    invoicePreviewNumber: string
  ): Observable<GenericResponseDto<InvoicePreviewDetailDto>> {
    return this._http.get<GenericResponseDto<InvoicePreviewDetailDto>>(
      this.invoicePreviewBaseURL +
        `/GetInvoicePreviewDetails?invoicePreviewNumber=` +
        invoicePreviewNumber
    );
  }

  getSalesOrderNotes(
    saleOrderId: string
  ): Observable<GenericResponseDto<OrderNotes[]>> {
    return this._http.get<GenericResponseDto<OrderNotes[]>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderNotes?SaleOrderId=` +
        saleOrderId
    );
  }

  getSalesOrderNotesWithTypeInvoiceProposal(
    invoiceProposalId: string
  ): Observable<GenericResponseDto<OrderNotes[]>> {
    return this._http.get<GenericResponseDto<OrderNotes[]>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderNotesWithTypeInvoiceProposal?InvoiceProposalId=` +
        invoiceProposalId
    );
  }

  getOrderLineDetails(
    invoicePreviewNumber: string,
    paging = 0
  ): Observable<GenericResponseDto<InvoicePreviewLines>> {
    return this._http.get<GenericResponseDto<InvoicePreviewLines>>(
      this.invoicePreviewBaseURL +
        `/GetInvoicePreviewLineDetails?invoicePreviewNumber=${invoicePreviewNumber}&paging=${paging}`
    );
  }

  getInvoicePreviewListing(): Observable<
    GenericResponseDto<InvoiceProposalDto[]>
  > {
    return this._http.get<GenericResponseDto<InvoiceProposalDto[]>>(
      this.saleOrderManagementBaseURL + `/GetInvoiceProposals`
    );
  }

  getInvoicePreviewListingPaginated(
    request: ProposalListRequestDto
  ): Observable<GenericResponseDto<InvoiceProposalDto[]>> {
    return this._http.post<GenericResponseDto<InvoiceProposalDto[]>>(
      this.invoicePreviewBaseURL + `/GetInvoiceProposalsPaginated`,
      request
    );
  }

  getInvoiceEmail(id: string): Observable<GenericResponseDto<InvoiceEmail[]>> {
    return this._http.get<GenericResponseDto<InvoiceEmail[]>>(
      this.invoicePreviewBaseURL +
        "/GetInvoiceEmailsBySalesOrderId?SalesOrderId=" +
        id
    );
  }

  getOrderTracking(
    payload: TrackingFreightApiRequestDto
  ): Observable<GenericResponseDto<TrackingFreightApiResponseDto>> {
    return this._http.post<GenericResponseDto<TrackingFreightApiResponseDto>>(
      `${this.d365SynchroniserBaseURL}/TrackingFreightAPI`,
      payload
    );
  }

  getFreightTrackingLine(
    oDataFilter: string
  ): Observable<GenericResponseDto<FreightTrackingsDetailValue[]>> {
    return this._http.get<GenericResponseDto<FreightTrackingsDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        `/GetFreightTrackings?odataFilter=${encodeURIComponent(oDataFilter)}`
    );
  }

  getBillingDetailsLine(
    oDataFilter: string
  ): Observable<GenericResponseDto<FreightBillingsDetailValue[]>> {
    return this._http.get<GenericResponseDto<FreightBillingsDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        `/GetFreightBillings?odataFilter=${encodeURIComponent(oDataFilter)}`
    );
  }

  getPurchaseOrderLine(
    SalesOrderId: string
  ): Observable<GenericResponseDto<PrintPurchaseOrderDto[]>> {
    return this._http.get<GenericResponseDto<PrintPurchaseOrderDto[]>>(
      this.saleOrderManagementBaseURL +
        "/GetPurchaseOrderPrintDetails?SaleOrderId=" +
        SalesOrderId
    );
  }

  getBillingDetailsForEmails(
    proposalId: string
  ): Observable<GenericResponseDto<GetBillingDetailsForEmailsDto>> {
    return this._http.get<GenericResponseDto<GetBillingDetailsForEmailsDto>>(
      this.saleOrderManagementBaseURL +
        "/GetBillingDetailsForEmails?proposalId=" +
        proposalId
    );
  }

  GetMyProfile(): Observable<GenericResponseDto<GetUserDto>> {
    return this._http.get<GenericResponseDto<GetUserDto>>(
      this.userManagementBaseURL + "/GetMyProfile"
    );
  }

  deleteInvoiceEmailData(
    payload: number[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.saleOrderManagementBaseURL + "/DeleteSalesOrderEmail", payload);
  }

  postFreightTrackingsDelete(
    payload: FreightTrackingsDetailValue
  ): Observable<GenericResponseDto<OdataDmlResponse>> {
    return this._http.post<GenericResponseDto<OdataDmlResponse>>(
      this.d365SynchroniserBaseURL + "/PostFreightTrackingsDelete",
      payload
    );
  }

  postFreightTrackingsUpdate(
    payload: PostFreightTrackingsModel
  ): Observable<GenericResponseDto<OdataDmlResponse>> {
    return this._http.post<GenericResponseDto<OdataDmlResponse>>(
      this.d365SynchroniserBaseURL + "/PostFreightTrackingsUpdate",
      payload
    );
  }

  postFreightTrackingsCreate(
    payload: PostFreightTrackingsModel
  ): Observable<GenericResponseDto<OdataDmlResponse>> {
    return this._http.post<GenericResponseDto<OdataDmlResponse>>(
      this.d365SynchroniserBaseURL + "/PostFreightTrackingsCreate",
      payload
    );
  }

  PostFreightTrackingsCreateExcel(
    payload: ProcessTrackingDetailsRequestDto
  ): Observable<GenericResponseDto<ProcessTrackingDetailsResponseDto>> {
    return this._http.post<
      GenericResponseDto<ProcessTrackingDetailsResponseDto>
    >(
      this.d365SynchroniserBaseURL + "/PostFreightTrackingsCreateExcel",
      payload
    );
  }

  createInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.invoicePreviewBaseURL + "/CreateSalesOrderInvoiceEmail", payload);
  }

  updateInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.invoicePreviewBaseURL + "/UpdateSalesOrderEmail", payload);
  }

  AddSalesOrderNotes(
    payload: SalesOrderNotes
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.saleOrderManagementBaseURL + "/AddSalesOrderNotes",
      payload
    );
  }

  GetProjectInvoicePrintPreviewReport(
    payload: ProjectInvoicePrintPreviewReportRequestDto
  ): Observable<
    GenericResponseDto<ProjectInvoicePrintPreviewReportResponseDto>
  > {
    return this._http.post<
      GenericResponseDto<ProjectInvoicePrintPreviewReportResponseDto>
    >(
      this.invoicePreviewBaseURL + "/GetProjectInvoicePrintPreviewReport",
      payload
    );
  }

  UpdateInvoicePreviewStatus(
    payload: UpdateInvoicePreviewStatusDto
  ): Observable<
    GenericResponseDto<UpdateProjectInvoicePreviewStatusResponseDto>
  > {
    return this._http.post<
      GenericResponseDto<UpdateProjectInvoicePreviewStatusResponseDto>
    >(this.invoicePreviewBaseURL + "/UpdateInvoicePreviewStatus", payload);
  }

  UpdateInvoicePreviewDispositionStatus(
    payload: UpdateInvoicePreviewDispositionStatusDto
  ): Observable<
    GenericResponseDto<UpdateProjectInvoicePreviewDispositionStatusResponseDto>
  > {
    return this._http.post<
      GenericResponseDto<UpdateProjectInvoicePreviewDispositionStatusResponseDto>
    >(
      this.invoicePreviewBaseURL + "/UpdateInvoicePreviewDispositionStatus",
      payload
    );
  }

  getFreightChargesInvoicePreview(
    odataFilter: string
  ): Observable<GenericResponseDto<FreightChargesInvoicePreviewDetailValue[]>> {
    return this._http.get<
      GenericResponseDto<FreightChargesInvoicePreviewDetailValue[]>
    >(
      this.d365SynchroniserBaseURL +
        "/FreightCharges?odataFilter=" +
        odataFilter
    );
  }

  getIpClientDeposit(
    odataFilter: string
  ): Observable<GenericResponseDto<IpClientDepositDetailValue[]>> {
    return this._http.get<GenericResponseDto<IpClientDepositDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        "/IpClientDeposit?odataFilter=" +
        odataFilter
    );
  }

  getFreightTrackingExcelTemplate(): Observable<Blob> {
    return this._http.get(
      this.saleOrderManagementBaseURL + `/GetFreightTrackingExcelTemplate`,
      {
        responseType: "blob",
      }
    );
  }
}
